/**
 * DisruptionsReport - Störungs-Auswertung
 * 
 * KPI Cards, Charts (Kategorie, Maschine/Ort), Tabelle mit Export
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Download,
  Loader2,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  FileSpreadsheet,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { HorizontalBarChart } from '../components/BarChart';
import { ExportButtonsPlaceholder } from '../components/ExportButtonsPlaceholder';
import { getChartLabel } from '../utils/chart-labels';
import {
  getTodayBerlin,
  getQuickPickerRange,
  type QuickPicker,
} from '@wash-ops/shared';
import { formatBerlinDateTimeDE } from '../utils/berlinDate';

type Preset = QuickPicker | 'custom';

/**
 * Formatiert einen YYYY-MM-DD String als DD.MM.YYYY
 */
function formatGermanDate(isoDate: string): string {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * Formatiert Minuten als h:mm
 * Edgecases: undefined/null/NaN → '—', negativ → '—', 0 → '0:00'
 */
function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  return `${h}:${m.toString().padStart(2, '0')}`;
}

interface DisruptionEntry {
  id: string;
  category: string;
  machineOrLocation: string;
  startedAt: string;
  endedAt: string | null;
  durationMinutes: number;
  status: 'open' | 'closed';
  recordedBy: string | null;
  description: string | null;
}

interface CategoryData {
  category: string;
  count: number;
  closedMinutes: number;
}

interface LocationData {
  label: string;
  count: number;
  closedMinutes: number;
}

interface Totals {
  totalCount: number;
  openCount: number;
  closedCount: number;
  openOver24h: number;
  openOver48h: number;
  closedMinutesTotal: number;
  openMinutesTotal: number;
  totalMinutes: number;
}

// Kategorie-Name: Snapshot wird vom Backend geliefert, kein Mapping nötig

export default function DisruptionsReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Date range from URL or default
  const urlFrom = searchParams.get('from');
  const urlTo = searchParams.get('to');

  const today = getTodayBerlin();

  // Determine initial preset
  const getInitialPreset = (): Preset => {
    if (!urlFrom || !urlTo) return 'today';
    const todayRange = getQuickPickerRange('today', new Date());
    if (urlFrom === todayRange.startDate && urlTo === todayRange.endDate) return 'today';
    const yesterdayRange = getQuickPickerRange('yesterday', new Date());
    if (urlFrom === yesterdayRange.startDate && urlTo === yesterdayRange.endDate) return 'yesterday';
    return 'custom';
  };

  const [preset, setPreset] = useState<Preset>(getInitialPreset());
  const [customStart, setCustomStart] = useState(urlFrom || '');
  const [customEnd, setCustomEnd] = useState(urlTo || '');

  // Compute effective date range
  const { fromDate, toDate } = useMemo(() => {
    if (preset === 'custom') {
      return { fromDate: customStart || today, toDate: customEnd || today };
    }
    const range = getQuickPickerRange(preset, new Date());
    return { fromDate: range.startDate, toDate: range.endDate };
  }, [preset, customStart, customEnd, today]);

  // Update URL when range changes
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('from', fromDate);
    params.set('to', toDate);
    setSearchParams(params, { replace: true });
  }, [fromDate, toDate, setSearchParams]);

  const handlePresetChange = (p: Preset) => {
    if (p !== 'custom') {
      setCustomStart('');
      setCustomEnd('');
    }
    setPreset(p);
  };

  // Data
  const [totals, setTotals] = useState<Totals | null>(null);
  const [byCategory, setByCategory] = useState<CategoryData[]>([]);
  const [byLocation, setByLocation] = useState<LocationData[]>([]);
  const [entries, setEntries] = useState<DisruptionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const LIMIT = 50;

  const loadSummary = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch(
        `/api/reports/disruptions/summary?from=${fromDate}&to=${toDate}`,
        { credentials: 'include' }
      );

      if (res.ok) {
        const data = await res.json();
        setTotals(data.totals || null);
        setByCategory(Array.isArray(data.byCategory) ? data.byCategory : []);
        setByLocation(Array.isArray(data.byMachineOrLocation) ? data.byMachineOrLocation : []);
      } else {
        setError('Fehler beim Laden der Daten');
      }
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  const loadList = async (pageNum: number) => {
    setListLoading(true);

    try {
      const res = await fetch(
        `/api/reports/disruptions/list?from=${fromDate}&to=${toDate}&page=${pageNum}&limit=${LIMIT}`,
        { credentials: 'include' }
      );

      if (res.ok) {
        const data = await res.json();
        setEntries(Array.isArray(data.items) ? data.items : []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalItems(data.pagination?.total || 0);
        setPage(pageNum);
      }
    } catch {
      // Silent fail for list
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    setPage(1);
    loadList(1);
  }, [fromDate, toDate]);

  // Export CSV
  const handleExport = () => {
    if (entries.length === 0) return;

    const headers = ['Kategorie', 'Maschine/Ort', 'Start', 'Ende', 'Dauer', 'Status', 'Erfasser', 'Beschreibung'];
    const rows = entries.map(e => [
      e.category || 'Unbekannt',
      e.machineOrLocation,
      e.startedAt ? formatBerlinDateTimeDE(e.startedAt) : '',
      e.endedAt ? formatBerlinDateTimeDE(e.endedAt) : '',
      formatDuration(e.durationMinutes),
      e.status === 'open' ? 'Offen' : 'Geschlossen',
      e.recordedBy || '',
      e.description || '',
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `stoerungen_${fromDate}_${toDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Störungs-Auswertung</h1>
            <p className="text-sm text-slate-500">Analyse und Export von Störungsdaten</p>
          </div>
          <ExportButtonsPlaceholder />
        </div>

        {/* Date Filter */}
        <div className="bg-white rounded-xl p-4 shadow-sm border space-y-4">
          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-2">
            {(['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'] as Preset[]).map((p) => (
              <button
                key={p}
                onClick={() => handlePresetChange(p)}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  preset === p
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                }`}
              >
                {p === 'today' && 'Heute'}
                {p === 'yesterday' && 'Gestern'}
                {p === 'thisWeek' && 'Diese Woche'}
                {p === 'lastWeek' && 'Letzte Woche'}
                {p === 'thisMonth' && 'Dieser Monat'}
                {p === 'lastMonth' && 'Letzter Monat'}
              </button>
            ))}
            <button
              onClick={() => handlePresetChange('custom')}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                preset === 'custom'
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
              }`}
            >
              Benutzerdefiniert
            </button>
          </div>

          {/* Custom Date Inputs */}
          {preset === 'custom' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
                />
              </div>
              <span className="text-slate-400">bis</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
              />
            </div>
          )}

          {/* Range Display */}
          <div className="text-sm text-slate-500">
            Zeitraum: {formatGermanDate(fromDate)} – {formatGermanDate(toDate)}
          </div>
        </div>

        {/* Loading / Error */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {/* Störungszeit Gesamt - Highlight KPI */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl shadow-sm p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-5 h-5 text-slate-300" />
                    <span className="text-sm text-slate-300">Störungszeit gesamt</span>
                  </div>
                  <p className="text-3xl font-bold">{formatDuration(totals?.totalMinutes ?? 0)}</p>
                  {(totals?.totalCount ?? 0) === 0 ? (
                    <p className="text-xs text-slate-400 mt-1">Keine Störungen im Zeitraum</p>
                  ) : (
                    <p className="text-xs text-slate-400 mt-1">
                      davon {formatDuration(totals?.closedMinutesTotal ?? 0)} abgeschlossen, {formatDuration(totals?.openMinutesTotal ?? 0)} noch offen
                    </p>
                  )}
                </div>
                <div className="text-right text-sm text-slate-400">
                  <div>{totals?.totalCount ?? 0} Störungen</div>
                  <div className="text-xs">{formatGermanDate(fromDate)} – {formatGermanDate(toDate)}</div>
                </div>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-500">Gesamt</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">{totals?.totalCount ?? 0}</p>
                <p className="text-xs text-slate-400">Störungen im Zeitraum</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <span className="text-sm text-slate-500">Offen</span>
                </div>
                <p className="text-2xl font-bold text-red-600">{totals?.openCount ?? 0}</p>
                <p className="text-xs text-slate-400">Noch nicht behoben</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span className="text-sm text-slate-500">&gt;24h offen</span>
                </div>
                <p className="text-2xl font-bold text-amber-600">{totals?.openOver24h ?? 0}</p>
                <p className="text-xs text-slate-400">Kritisch lange offen</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-slate-500">Geschlossen</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{totals?.closedCount ?? 0}</p>
                <p className="text-xs text-slate-400">{formatDuration(totals?.closedMinutesTotal ?? 0)} Stillstand</p>
              </div>
            </div>

            {/* Charts Grid - responsive: 1 Spalte auf small, 2 ab lg */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* By Category - Count */}
              <div className="min-w-0">
                <HorizontalBarChart
                  data={byCategory.map(c => ({
                    name: c.category || 'Unbekannt',
                    fullName: c.category || 'Unbekannt',
                    value: c.count,
                    id: c.category,
                  }))}
                  title="Störungen nach Kategorie (Anzahl)"
                  unit=""
                  height={250}
                  maxItems={10}
                  emptyMessage="Keine Kategorie-Daten"
                />
              </div>

              {/* By Category - Minutes */}
              <div className="min-w-0">
                <HorizontalBarChart
                  data={byCategory.filter(c => c.closedMinutes > 0).map(c => ({
                    name: c.category || 'Unbekannt',
                    fullName: c.category || 'Unbekannt',
                    value: c.closedMinutes,
                    id: c.category,
                  }))}
                  title="Stillstand nach Kategorie (Minuten, geschlossen)"
                  unit="min"
                  height={250}
                  maxItems={10}
                  emptyMessage="Keine Minuten-Daten"
                />
              </div>

              {/* By Location - Count */}
              <div className="min-w-0">
                <HorizontalBarChart
                  data={byLocation.slice(0, 10).map(l => ({
                    name: getChartLabel(l.label),
                    fullName: l.label,
                    value: l.count,
                    id: l.label,
                  }))}
                  title="Top 10 Maschinen/Orte (Anzahl)"
                  unit=""
                  height={250}
                  maxItems={10}
                  emptyMessage="Keine Ort-Daten"
                />
              </div>

              {/* By Location - Minutes */}
              <div className="min-w-0">
                <HorizontalBarChart
                  data={byLocation.filter(l => l.closedMinutes > 0).slice(0, 10).map(l => ({
                    name: getChartLabel(l.label),
                    fullName: l.label,
                    value: l.closedMinutes,
                    id: l.label,
                  }))}
                  title="Top 10 Maschinen/Orte (Minuten, geschlossen)"
                  unit="min"
                  height={250}
                  maxItems={10}
                  emptyMessage="Keine Minuten-Daten"
                />
              </div>
            </div>

            {/* Entries Table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-700">
                  Störungen ({totalItems})
                </h3>
                {listLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Kategorie
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Maschine/Ort
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Start
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Ende
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Dauer
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {entries.length === 0 && !listLoading && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                          Keine Störungen im Zeitraum
                        </td>
                      </tr>
                    )}
                    {entries.map((entry) => (
                      <tr key={entry.id} className={entry.status === 'open' ? 'bg-red-50' : ''}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {entry.category || 'Unbekannt'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {entry.machineOrLocation}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {entry.startedAt ? formatBerlinDateTimeDE(entry.startedAt) : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {entry.endedAt ? formatBerlinDateTimeDE(entry.endedAt) : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                          {formatDuration(entry.durationMinutes)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {entry.status === 'open' ? (
                            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                              Offen
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                              Geschlossen
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Seite {page} von {totalPages} ({totalItems} Einträge)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadList(page - 1)}
                      disabled={page <= 1 || listLoading}
                      className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Zurück
                    </button>
                    <button
                      onClick={() => loadList(page + 1)}
                      disabled={page >= totalPages || listLoading}
                      className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Weiter
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
